import type {
	Lot,
	Pop,
	PopProfile,
	Project,
	Request,
	ReturnFreightInfo,
	ReturnFreightRequest
} from './data.model'
import { async_get, async_post } from '$utils/fetchUtilsAsync'
import { SvelteURL } from 'svelte/reactivity'
import { getAbortSignal } from 'svelte'
import { miniLotToLot } from '$mapapp/types/miniLot'
import { appState } from '$mapapp/stores/appstate.svelte'
import {
	getTutorialLotDetail,
	getTutorialPopDetail,
	getTutorialProfileDetail
} from '$mapapp/tutorial/tutorialFixtures.svelte'

/**
 * Returns the relevant project information for the given project id.
 * @param projectId {string}
 * @returns Promise<Project>
 */
export async function getProjectInformation(projectId: string) {
	const data = await async_get(
		new SvelteURL(`/api/map/project/${projectId}/info`, window.location.origin),
		false,
		{ signal: getAbortSignal() }
	)
	return data as Project
}

/**
 * Returns the extended lot information for the given lot id. Necessary for the slide in.
 * @param lotId {string}
 * @returns Promise
 */
export async function getLotInformation(lotId: string): Promise<Lot> {
	if (
		lotId === 'minilot' &&
		appState.isMatching &&
		appState.matching &&
		appState.matching.miniLot
	) {
		return new Promise<Lot>((resolve) => {
			resolve(miniLotToLot(appState.matching!.miniLot!))
		})
	}
	// Tutorial fixture lots short-circuit the network — IDs are prefixed `tut-`
	// and only exist while the tutorial overlay is open.
	const fixture = getTutorialLotDetail(lotId)
	if (fixture) return fixture
	const data = await async_get(
		new SvelteURL(`/api/map/lot/${lotId}/info`, window.location.origin),
		false,
		{ signal: getAbortSignal() }
	)
	return data as Lot
}

/**
 * Returns the extended pop information for the given pop id, including the profiles. Necessary for the slide in.
 * Must be called from within a $derived or $effect context (for abort signal to work).
 * @param popId {string}
 * @returns Promise
 */
export async function getPopInformation(popId: string): Promise<Pop> {
	// Tutorial fixture pops short-circuit the network — IDs are prefixed `tut-`
	// and only exist while the tutorial overlay is open.
	const fixture = getTutorialPopDetail(popId)
	if (fixture) return fixture
	const data = await async_get(
		new SvelteURL(`/api/map/pop/${popId}/info`, window.location.origin),
		false,
		{ signal: getAbortSignal() }
	)
	return data as Pop
}

/**
 * Returns the extended profile information for the given profile id, including the profiles. Necessary for the slide in.
 * Must be called from within a $derived or $effect context (for abort signal to work).
 * @param profileId {string}
 * @returns Promise
 */
export async function getProfileInformation(profileId: string): Promise<PopProfile> {
	// Tutorial fixture profiles short-circuit the network — IDs are prefixed `tut`
	// and only exist while the tutorial overlay is open.
	const fixture = getTutorialProfileDetail(profileId)
	if (fixture) return fixture
	const data = await async_get(
		new SvelteURL(`/api/map/profile/${profileId}/info`, window.location.origin),
		false,
		{ signal: getAbortSignal() }
	)
	return data as PopProfile
}

/**
 * Returns the extended Request information for the given request id. Necessary for the slide in.
 * Must be called from within a $derived or $effect context (for abort signal to work).
 * @param requestId {string}
 * @returns Promise
 */
export async function getRequestInformation(requestId: string): Promise<Request> {
	const data = await async_get(
		new SvelteURL(`/api/map/request/${requestId}/info`, window.location.origin),
		false,
		{ signal: getAbortSignal() }
	)
	return data as Request
}

/**
 * Returns the return freight candidates for the given match.
 * Must be called from within a $derived or $effect context (for abort signal to work).
 * @param request {ReturnFreightRequest}
 * @returns Promise<ReturnFreightInfo>
 */
export async function getReturnFreightInformation(
	request: ReturnFreightRequest
): Promise<ReturnFreightInfo> {
	return async_post('/api/map/returnFreight', request, false, { signal: getAbortSignal() })
}